// After arrival: the glide in to the Clockers 64, the sharp close-up, and the hand-over from the
// film's screen to ours (terminal + CRT pass + clean-glass plate), then the boot.
//
// Geometry is in source pixels (3840 x 2160) and matches tools/encode_media.py (CRT, close crop)
// and tools/make_plate.py (plate rectangle). Everything here is placed inside #stage.
//
// Nothing heavy exists until a supported device actually arrives: the canvases, the WebGL context
// and the plate are created in `build()`. While the device notice is up the scene is suspended
// (videos, CRT, boot clock and input), and every asynchronous step waits at the gate. Every wait
// for optional media is bounded, so nothing can keep the visitor from the calendar.

import type { Rect } from "./media";
import type { Stage } from "./stage";
import { FULL } from "./stage";
import { GRID, H as SCREEN_H, Terminal, W as SCREEN_W } from "./screen/terminal";
import { CRT } from "./screen/crt";
import { Boot } from "./screen/boot";
import { Program } from "./screen/program";
import { doorStates, loadCalendar, msToNextMidnight, type Calendar, type Door } from "./games";

/** CRT glass: the dark curved screen inside the bezel (encode_media.py CRT). */
export const GLASS: Rect = { x: 1500, y: 615, w: 855, h: 590 };
/** Close-up crop: 16:9 around the CRT (encode_media.py close). */
export const CLOSE: Rect = { x: 985, y: 380, w: 1884, h: 1060 };
/** Clean-glass plate over the film's screen text (make_plate.py RECT). */
export const PLATE: Rect = { x: 1530, y: 650, w: 720, h: 200 };
/** The Clockers 64's own keys, in the film (measured on the 4K final frame). */
const KEYS: Array<{ key: string; r: Rect }> = [
  { key: "F1", r: { x: 2685, y: 1610, w: 100, h: 52 } },
  { key: "F2", r: { x: 2705, y: 1662, w: 105, h: 53 } },
  { key: "F3", r: { x: 2725, y: 1715, w: 107, h: 52 } },
  { key: "F4", r: { x: 2747, y: 1767, w: 113, h: 78 } },
  { key: "Enter", r: { x: 2420, y: 1662, w: 217, h: 108 } },
];

/** Pause on the room before the glide starts. */
const SETTLE_MS = 900;
/** How long the optional close-up may take before we go on without it. */
const CLOSE_WAIT_MS = 2500;

function place(el: HTMLElement, r: Rect): void {
  el.style.left = `${r.x}px`;
  el.style.top = `${r.y}px`;
  el.style.width = `${r.w}px`;
  el.style.height = `${r.h}px`;
}

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

/** Resolve with the promise's value, or `fallback` after `ms`. */
function within<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([p, sleep(ms).then(() => fallback)]);
}

/** Wait for a media event, bounded; true if it came. */
function mediaEvent(el: HTMLMediaElement, ok: string, ms: number): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (v: boolean) => {
      el.removeEventListener(ok, yes);
      el.removeEventListener("error", no);
      window.clearTimeout(id);
      resolve(v);
    };
    const yes = () => done(true);
    const no = () => done(false);
    const id = window.setTimeout(() => done(false), ms);
    el.addEventListener(ok, yes);
    el.addEventListener("error", no);
  });
}

export interface SceneDeps {
  stage: Stage;
  stageEl: HTMLElement;
  appEl: HTMLElement;
  loopVid: HTMLVideoElement;
  reducedMotion: MediaQueryList;
}

interface Parts {
  closeVid: HTMLVideoElement;
  closeStill: HTMLImageElement;
  plate: HTMLImageElement;
  /** true when the plate decoded in time; false: use the erasure patch (.plate-fallback) */
  plateReady: Promise<boolean>;
  screenEl: HTMLDivElement;
  log: HTMLDivElement;
  term: Terminal;
  crt: CRT;
  program: Program;
  zoomBtn: HTMLButtonElement;
}

/** Which picture layer owns playback after the glide. The wide loop's source is kept (paused) for
 *  a later zoom-out (milestone 3). */
type Layer = "wide" | "close-video" | "close-still";

export class Scene {
  private p: Parts | null = null;
  private boot: Boot | null = null;
  private closeReady: Promise<string | null> = Promise.resolve(null);
  private started = false;
  private suspended = false;
  private gateWaiters: Array<() => void> = [];
  private layer: Layer = "wide";
  private calendar: Calendar | null = null;
  private zoomedOut = false;
  private zooming = false;
  /** the one pending departure to a game, cancelled if the page comes back */
  private leaveTimer = 0;
  private midnightTimer = 0;
  /** "room" -> "gliding" -> "close" -> "screen" */
  state: "room" | "gliding" | "close" | "screen" = "room";

  constructor(private d: SceneDeps) {
    window.addEventListener("jks:arrived", () => void this.enter());
    window.addEventListener("keydown", (ev) => this.skipKey(ev));
    // back from a game via the browser's page cache: the program waits in its LOAD state
    window.addEventListener("pageshow", (ev) => {
      if (!ev.persisted) return;
      window.clearTimeout(this.leaveTimer); // a departure queued before Back must not fire now
      this.leaveTimer = 0;
      this.p?.program.ready();
      this.wake(); // the departure stopped the screen and the videos
      void this.refreshDoors(); // the date may have changed while the page was in the cache
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void this.refreshDoors();
    });
  }

  /** The close-up loop's blob URL, when main.ts has downloaded it. */
  setCloseSource(p: Promise<string | null>): void {
    this.closeReady = p;
  }

  /** True once the scene has taken over the picture from the wide arrival loop. */
  get ownsPicture(): boolean {
    return this.layer !== "wide";
  }

  // ---------- suspension (the device notice) ----------

  setSuspended(on: boolean): void {
    if (on === this.suspended) return;
    this.suspended = on;
    const p = this.p;
    if (on) {
      p?.closeVid.pause();
      p?.crt.stop();
      this.boot?.pause();
    } else {
      if (p && this.layer === "close-video") void p.closeVid.play().catch(() => undefined);
      if (p && this.state === "screen") p.crt.start();
      this.boot?.resume();
      const w = this.gateWaiters;
      this.gateWaiters = [];
      w.forEach((r) => r());
    }
  }

  /** Resolves when the scene is not suspended. */
  private gate(): Promise<void> {
    return this.suspended ? new Promise((r) => this.gateWaiters.push(r)) : Promise.resolve();
  }

  private skipKey(ev: KeyboardEvent): void {
    if (this.suspended || this.state !== "screen" || !this.boot) return;
    if (ev.metaKey || ev.ctrlKey || ev.altKey || ev.key === "Tab") return;
    if (!this.boot.done) {
      this.boot.skip();
      return;
    }
    // a focused button (the accessible twin, the controls) handles its own Enter and Space
    const onButton = ev.target instanceof HTMLButtonElement;
    if (onButton && (ev.key === "Enter" || ev.key === " ")) return;
    if (this.p?.program.key(ev.key)) ev.preventDefault();
  }

  /** Grid cell under a window position, through the CRT's barrel curvature. */
  private cellAt(clientX: number, clientY: number): { col: number; row: number } | null {
    const p = this.p;
    if (!p) return null;
    const r = p.screenEl.getBoundingClientRect();
    const u = (clientX - r.left) / r.width - 0.5;
    const v = (clientY - r.top) / r.height - 0.5;
    // the shader samples the terminal at barrel(uv); the plain fallback canvas is not curved
    const k = 1 + (p.crt.webgl ? p.crt.curvature : 0) * (u * u + v * v);
    const x = (0.5 + u * k) * SCREEN_W;
    const y = (0.5 + v * k) * SCREEN_H;
    const col = Math.floor((x - GRID.x0) / GRID.cw);
    const row = Math.floor((y - GRID.y0) / GRID.ch);
    if (col < 0 || row < 0 || col >= GRID.cols || row >= GRID.rows) return null;
    return { col, row };
  }

  // ---------- building (only on a supported device that has arrived) ----------

  private build(): Parts {
    if (this.p) return this.p;
    const mk = <T extends HTMLElement>(tag: string, id: string) => {
      const el = document.createElement(tag) as T;
      el.id = id;
      el.setAttribute("aria-hidden", "true");
      return el;
    };
    const closeVid = mk<HTMLVideoElement>("video", "close");
    closeVid.muted = true;
    closeVid.loop = true;
    closeVid.playsInline = true;
    const closeStill = mk<HTMLImageElement>("img", "close-still");
    closeStill.alt = "";
    closeStill.decoding = "async";
    closeStill.src = "./media/close-1920.jpg";
    const plate = mk<HTMLImageElement>("img", "plate");
    plate.alt = "";
    plate.decoding = "async";
    plate.src = "./media/plate.png";
    // if the plate cannot be had, an opaque patch of glass colour erases the film's text instead
    const plateReady = within(plate.decode().then(() => true, () => false), CLOSE_WAIT_MS, false);
    const screenEl = document.createElement("div");
    screenEl.id = "screen";
    const term = new Terminal();
    term.blinkAllowed = !this.d.reducedMotion.matches;
    const crt = new CRT(term);
    crt.flicker = !this.d.reducedMotion.matches;
    crt.el.setAttribute("aria-hidden", "true");
    const log = document.createElement("div");
    log.className = "sr-only";
    log.setAttribute("role", "log");
    log.setAttribute("aria-label", "Datorns skärm");
    const program = new Program(term, { open: (d) => this.openGame(d) });
    screenEl.append(crt.el, log, program.a11y);
    place(closeVid, CLOSE);
    place(closeStill, CLOSE);
    place(plate, PLATE);
    place(screenEl, GLASS);
    this.d.stageEl.append(closeVid, closeStill, plate, screenEl);
    screenEl.addEventListener("click", (ev) => {
      if (this.suspended || !this.boot) return;
      if (!this.boot.done) return this.boot.skip();
      const c = this.cellAt(ev.clientX, ev.clientY);
      if (c) program.pointer(c.col, c.row, true);
    });
    screenEl.addEventListener("mousemove", (ev) => {
      if (this.suspended || !this.boot?.done) return;
      const c = this.cellAt(ev.clientX, ev.clientY);
      if (c) program.pointer(c.col, c.row, false);
    });
    // the computer's own keys in the picture (seen when zoomed out)
    for (const k of KEYS) {
      const hs = document.createElement("div");
      hs.className = "hotspot";
      hs.setAttribute("aria-hidden", "true");
      hs.title = k.key === "Enter" ? "RETURN" : k.key;
      place(hs, k.r);
      hs.addEventListener("click", () => {
        if (!this.suspended && this.boot?.done) program.key(k.key);
      });
      this.d.stageEl.append(hs);
    }
    const zoomBtn = document.createElement("button");
    zoomBtn.type = "button";
    zoomBtn.id = "zoom";
    zoomBtn.hidden = true;
    zoomBtn.textContent = "Zooma ut";
    zoomBtn.addEventListener("click", () => void this.toggleZoom());
    document.getElementById("controls")?.prepend(zoomBtn);
    window.addEventListener("resize", () => this.sizeScreen());
    this.watchPixelRatio();
    this.d.reducedMotion.addEventListener("change", () => {
      term.blinkAllowed = !this.d.reducedMotion.matches;
      crt.flicker = !this.d.reducedMotion.matches;
      term.dirty = true;
    });
    this.p = { closeVid, closeStill, plate, plateReady, screenEl, log, term, crt, program, zoomBtn };
    return this.p;
  }

  /** A device-pixel-ratio change (display scaling) without a CSS resize must resize too. */
  private watchPixelRatio(): void {
    const arm = () => {
      const mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      mq.addEventListener(
        "change",
        () => {
          this.sizeScreen();
          arm();
        },
        { once: true },
      );
    };
    arm();
  }

  // ---------- the sequence ----------

  private async enter(): Promise<void> {
    if (this.started) return;
    this.started = true;
    await this.gate();
    const p = this.build();
    const reduced = this.d.reducedMotion.matches;
    const doorsReady = loadCalendar().then(
      (c) => (this.calendar = c),
      (e) => (console.warn("[julkalenderspel] games.json unavailable", e), null),
    );
    await sleep(reduced ? 0 : SETTLE_MS);
    await this.gate();

    // 1. the glide
    this.state = "gliding";
    await this.d.stage.glide(CLOSE, !reduced);
    await this.gate();

    // 2. the sharp close-up: the loop, in step with the wide loop; else the still; else the
    //    scaled wide picture stays. Each step is bounded.
    this.state = "close";
    const wide = this.d.loopVid;
    const url = reduced ? null : await within(this.closeReady, CLOSE_WAIT_MS, null);
    await this.gate();
    if (url && (await this.startClose(url))) {
      this.layer = "close-video";
      this.d.appEl.classList.add("close-on");
    } else {
      const ok = await within(p.closeStill.decode().then(() => true, () => false), CLOSE_WAIT_MS, false);
      await this.gate();
      if (ok) {
        this.layer = "close-still";
        this.d.appEl.classList.add("close-still-on");
      }
    }
    if (this.layer !== "wide") window.setTimeout(() => wide.pause(), 500); // covered after the fade
    await this.gate();

    // 3. our screen takes over from the film's. The film's lettering is smooth and ours is the
    //    Clockers 64's pixel font, so the take-over is a deliberate CRT refresh: one short flicker
    //    during which the plate and our text replace the film's.
    if (new URLSearchParams(location.search).get("hold") === "close") {
      await new Promise<void>((r) => ((window as unknown as { __release: () => void }).__release = r));
    }
    const plateOk = await p.plateReady;
    await this.gate();
    this.d.appEl.classList.add(plateOk ? "plate-ok" : "plate-fallback");
    this.sizeScreen();
    this.state = "screen";
    if (!this.suspended) p.crt.start();
    this.d.appEl.classList.add("screen-on");
    if (!reduced) await this.refresh(p.crt);
    await this.gate();
    p.log.textContent = "";
    this.say("CLOCKERS 64 BASIC V2");
    this.say("READY.");
    window.dispatchEvent(new CustomEvent("jks:screen"));

    // 4. the boot
    this.boot = new Boot(p.term, {
      line: (t) => this.say(t),
      clearing: () => void this.flash(p.crt),
      program: async (_term, step) => {
        await within(doorsReady, 5000, null);
        const drawnWithData = !!this.calendar; // decided before the draw: data may land during it
        p.program.setDoors(drawnWithData ? doorStates(this.calendar!) : []);
        await p.program.drawIn(step);
        // data that arrives after the bounded wait (or during the draw) still fills the screen
        if (!drawnWithData)
          void doorsReady.then((c) => {
            if (c) {
              p.program.setDoors(doorStates(c));
              this.scheduleMidnight();
            }
          });
      },
    });
    if (this.suspended) this.boot.pause();
    await this.boot.run();
    const open = p.program.info() as { open: number[] };
    this.say(`Julkalendern är igång. ${open.open.length ? `Öppna luckor: ${open.open.join(", ")}.` : "Inga luckor är öppna än."} Välj med tabb eller piltangenterna.`);
    p.zoomBtn.hidden = false;
    this.scheduleMidnight();
    window.dispatchEvent(new CustomEvent("jks:program"));
  }

  /**
   * Start the close-up so that it continues from the wide loop's current frame: freeze the wide
   * loop, seek the close-up to the same time, wait until that frame is ready, then play and reveal.
   * The crossfade is then from a still frame to the same frame in motion: no doubled snow.
   */
  private async startClose(url: string): Promise<boolean> {
    const v = this.p!.closeVid;
    const wide = this.d.loopVid;
    v.src = url;
    if (!(await mediaEvent(v, "loadeddata", CLOSE_WAIT_MS))) return false;
    await this.gate(); // the notice may have come up while the close-up decoded
    const playing = !wide.paused && wide.duration > 0;
    if (playing) wide.pause();
    const t = playing ? wide.currentTime % (v.duration || wide.duration) : 0;
    v.currentTime = t;
    await mediaEvent(v, "seeked", 1000);
    await this.gate();
    try {
      await within(v.play(), CLOSE_WAIT_MS, undefined);
    } catch {
      if (playing && !this.suspended) void wide.play().catch(() => undefined);
      return false;
    }
    if (this.suspended) {
      // suspended during play(): hold the close-up; setSuspended(false) resumes it
      v.pause();
      return true;
    }
    if (v.paused) {
      if (playing) void wide.play().catch(() => undefined);
      return false;
    }
    return true;
  }

  private say(text: string): void {
    const p = document.createElement("p");
    p.textContent = text;
    this.p?.log.append(p);
  }

  /** Animate crt.power through `curve(k)` for `ms`, on the scene's gate. */
  private animatePower(crt: CRT, ms: number, curve: (k: number) => number): Promise<void> {
    return new Promise((resolve) => {
      let elapsed = 0;
      let last = performance.now();
      const run = (t: number) => {
        if (!this.suspended) elapsed += t - last; // the animation's clock stops under the notice
        last = t;
        const k = Math.min(1, elapsed / ms);
        crt.power = curve(k);
        if (k < 1) requestAnimationFrame(run);
        else {
          crt.power = 1;
          resolve();
        }
      };
      requestAnimationFrame(run);
    });
  }

  /** The take-over: a quick dip and back, like a CRT catching its sync. */
  private refresh(crt: CRT): Promise<void> {
    return this.animatePower(crt, 260, (k) => (k < 0.3 ? 1 - (k / 0.3) * 0.75 : 0.25 + ((k - 0.3) / 0.7) * 0.75));
  }

  /** The screen clears with a brief collapse and flash, as the program takes over. */
  private flash(crt: CRT): Promise<void> {
    if (this.d.reducedMotion.matches) return Promise.resolve();
    return this.animatePower(crt, 520, (k) => (k < 0.35 ? 1 - (k / 0.35) * 0.9 : 0.1 + ((k - 0.35) / 0.65) * 0.9));
  }

  /** The WebGL drawing buffer matches the glass's size on screen, in device pixels. */
  private sizeScreen(): void {
    if (!this.p) return;
    const s = this.d.stage.scale();
    const dpr = window.devicePixelRatio || 1;
    this.p.crt.setPixelSize(GLASS.w * s * dpr, GLASS.h * s * dpr);
  }

  /** Back from a game: restart what the departure stopped (render loop, the visible video). */
  private wake(): void {
    const p = this.p;
    if (!p || this.suspended) return;
    if (this.state === "screen") p.crt.start();
    if (this.layer === "close-video") void p.closeVid.play().catch(() => undefined);
    if (this.zoomedOut && this.d.loopVid.src && !this.d.reducedMotion.matches) void this.d.loopVid.play().catch(() => undefined);
  }

  /** At midnight (Swedish time) the next door opens by itself. One timer, re-armed each time. */
  private scheduleMidnight(): void {
    if (!this.calendar) return;
    window.clearTimeout(this.midnightTimer);
    this.midnightTimer = window.setTimeout(() => void this.refreshDoors(), msToNextMidnight(this.calendar.timezone));
  }

  /**
   * Re-read the calendar and recompute the doors for today (at midnight, back from the page
   * cache, a tab becoming visible). Milestone 4's server function returns the doors released so
   * far, so this re-fetch is also how newly opened games arrive.
   */
  private async refreshDoors(): Promise<void> {
    const p = this.p;
    if (!p || !this.boot?.done) return;
    try {
      this.calendar = await loadCalendar();
    } catch (e) {
      console.warn("[julkalenderspel] games.json refresh failed; keeping the last data", e);
    }
    if (this.calendar) p.program.setDoors(doorStates(this.calendar));
    this.scheduleMidnight();
  }

  /** Hand-off to a game: the screen shows LOAD, the music fades, then the browser goes there. */
  private openGame(d: Door): void {
    if (this.leaveTimer) return;
    window.dispatchEvent(new CustomEvent("jks:leaving", { detail: { day: d.day } }));
    this.leaveTimer = window.setTimeout(() => {
      this.leaveTimer = 0;
      // Let go of everything that plays before leaving (audio, decoders, the render loop), so the
      // browser has nothing to tear down while it switches documents, then go.
      window.dispatchEvent(new CustomEvent("jks:depart"));
      this.p?.crt.stop();
      this.p?.closeVid.pause();
      this.d.loopVid.pause();
      location.assign(d.game!.url);
    }, 900);
  }

  /** Zoom out to the whole desk (the keyboard's keys work there) and back in. */
  private async toggleZoom(): Promise<void> {
    const p = this.p;
    if (!p || this.zooming || this.suspended) return;
    this.zooming = true;
    const out = !this.zoomedOut;
    const reduced = this.d.reducedMotion.matches;
    const wide = this.d.loopVid;
    if (out && !reduced && wide.src) void wide.play().catch(() => undefined); // the room moves again around the close-up
    await this.d.stage.glide(out ? FULL : CLOSE, !reduced);
    if (!out) wide.pause();
    this.sizeScreen();
    this.zoomedOut = out;
    this.d.appEl.classList.toggle("zoomed-out", out);
    p.zoomBtn.textContent = out ? "Zooma in" : "Zooma ut";
    this.zooming = false;
  }

  /** For the headless checks. */
  info(): object {
    const p = this.p;
    return {
      state: this.state,
      built: !!p,
      layer: this.layer,
      suspended: this.suspended,
      webgl: p?.crt.webgl ?? false,
      crtRunning: p?.crt.running ?? false,
      pixelSize: p?.crt.pixelSize ?? null,
      booted: !!this.boot?.done,
      mode: p?.term.mode ?? null,
      blink: p ? p.term.blinkAllowed : null,
      program: p?.program.info() ?? null,
      zoomedOut: this.zoomedOut,
    };
  }

  /** For the headless checks: lose the WebGL context. */
  loseContext(): boolean {
    return this.p?.crt.loseContext() ?? false;
  }
}
